"use client"

import { useState } from "react"
import Link from "next/link"
import { Tag, Ticket, Gift, Copy, Check, CalendarClock, Info, Hourglass, Zap, MapPin, Ban } from "lucide-react"
import type { Promotion } from "@/lib/api"
import { formatPrice } from "@sw-business-solutions/cst-mobikart-core/lib/berlin-time"
import {
  describeDiscountConditions,
  describePromoEffect,
} from "@sw-business-solutions/cst-mobikart-core/lib/pricing-conditions"

function BudgetBar({ budget }: { budget: NonNullable<Promotion["budget"]> }) {
  const remaining = Math.max(0, budget.total - budget.used)
  const pct = budget.total > 0 ? Math.min(100, Math.round((budget.used / budget.total) * 100)) : 0
  const remainingText =
    budget.kind === "AMOUNT"
      ? `noch ${formatPrice(remaining)} verfügbar`
      : `noch ${remaining} von ${budget.total} verfügbar`
  const empty = remaining <= 0
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-medium text-gray-500">{empty ? "Kontingent ausgeschöpft" : "Aktionstopf"}</span>
        <span className={empty ? "font-semibold text-red-500" : "font-semibold text-gray-700"}>{remainingText}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full transition-all ${empty ? "bg-red-400" : pct > 80 ? "bg-orange-400" : "bg-green-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function formatValidTo(iso?: string): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })
}

function CouponCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* Zwischenablage nicht verfügbar — Code steht sichtbar da, kein Drama. */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-2 rounded-lg border-2 border-dashed border-[var(--main-color)] bg-[var(--main-color)]/5 px-3 py-1.5 font-mono text-sm font-bold text-[var(--main-color)] transition-colors hover:bg-[var(--main-color)]/10"
      title="Code kopieren"
    >
      {code}
      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 opacity-70" />}
    </button>
  )
}

function PromotionCard({ promo }: { promo: Promotion }) {
  const isCoupon = promo.kind === "COUPON"
  const isCampaign = promo.kind === "CAMPAIGN"
  const conditions = describeDiscountConditions(promo.conditions)
  const validTo = formatValidTo(promo.validTo)

  const icon = isCoupon ? <Ticket className="h-5 w-5" /> : isCampaign ? <Gift className="h-5 w-5" /> : <Tag className="h-5 w-5" />
  const iconBg = isCoupon ? "bg-violet-100 text-violet-600" : isCampaign ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-600"

  return (
    <div className="flex flex-col rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconBg}`}>{icon}</span>
          <h3 className="text-lg font-bold text-gray-900">{promo.name}</h3>
        </div>
        {promo.effect && (
          <span className="shrink-0 rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-700">
            {describePromoEffect(promo.effect)}
          </span>
        )}
      </div>

      {promo.limited && !promo.budget && (
        <div className="mb-3 inline-flex w-fit items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-700">
          <Hourglass className="h-3.5 w-3.5" /> Nur begrenzt verfügbar
        </div>
      )}

      {promo.description && <p className="mb-3 text-sm text-gray-600">{promo.description}</p>}

      {promo.budget && <BudgetBar budget={promo.budget} />}

      {isCoupon && promo.code && (
        <div className="mb-3">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">Gutscheincode</span>
          <CouponCode code={promo.code} />
        </div>
      )}

      {/* PER_UNIT-Kampagne: was es je Einheit gibt („5,00 € pro 1er") */}
      {promo.rewardUnits && promo.rewardUnits.length > 0 && (
        <div className="mb-3">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">Das gibt es</span>
          <ul className="space-y-0.5 text-sm text-gray-700">
            {promo.rewardUnits.map((u) => (
              <li key={u.label} className="flex items-baseline gap-1.5">
                <span className="font-semibold text-green-700">
                  {u.type === "PERCENTAGE" ? `${u.amount} %` : formatPrice(u.amount)}
                </span>
                <span className="text-gray-500">pro {u.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {promo.fineprint && promo.fineprint.length > 0 && (
        <p className="mb-3 text-xs text-gray-400">{promo.fineprint.join(" · ")}</p>
      )}

      {isCampaign && promo.ctaNote && (
        <div className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-lg bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-700">
          <MapPin className="h-4 w-4" /> {promo.ctaNote}
        </div>
      )}

      {conditions.length > 0 && (
        <div className="mb-3">
          <span className="mb-1 flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-gray-400">
            <Info className="h-3 w-3" /> Bedingungen
          </span>
          <ul className="list-disc space-y-0.5 pl-5 text-sm text-gray-600">
            {conditions.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Zeitanteilige Aktionen brauchen mehr als ein Stichwort in der
          Bedingungsliste: „5 % Rabatt, 20–22 Uhr" liest sich sonst wie ein
          Versprechen, das die Buchung nicht haelt. Ein Zwei-Stunden-Paket ab
          18:30 Uhr bekommt 15 statt 60 Euro — richtig gerechnet, aber ohne
          diesen Satz unerklaerlich. */}
      {promo.conditions?.zeitanteilig && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
          <span className="mb-1 flex items-center gap-1.5 font-semibold">
            <Info className="h-3.5 w-3.5 shrink-0" /> So wird gerechnet
          </span>
          Der Rabatt gilt für den Teil deiner Fahrzeit, der wirklich im Aktionsfenster liegt.
          Beginnt deine Buchung vorher, wird sie anteilig berücksichtigt — je mehr Zeit im
          Fenster liegt, desto höher der Rabatt. Den genauen Betrag siehst du bei der
          Zeitauswahl, bevor du buchst.
        </div>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 text-xs text-gray-400">
        {validTo && (
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" /> Gültig bis {validTo}
          </span>
        )}
        {promo.kind === "RULE" && <span>Wird im Buchungsschritt automatisch abgezogen</span>}
        {promo.nonCombinable && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-600">
            <Ban className="h-3 w-3" /> Nicht kombinierbar
          </span>
        )}
      </div>
    </div>
  )
}

function BoerseHint({ maxDiscountPercent }: { maxDiscountPercent: number }) {
  return (
    <Link
      href="/boerse"
      className="mb-10 flex flex-col gap-2 rounded-2xl border border-[var(--main-color)]/30 bg-gradient-to-r from-[var(--main-color)]/10 to-orange-50 p-6 transition-shadow hover:shadow-md sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--main-color)] text-white">
          <Zap className="h-6 w-6" />
        </span>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Last-Minute in der Slotbörse</h3>
          <p className="text-sm text-gray-600">
            Kurzentschlossen? Sichere dir freie Slots mit bis zu {Math.round(maxDiscountPercent)} % Rabatt.
          </p>
        </div>
      </div>
      <span className="shrink-0 rounded-lg bg-[var(--main-color)] px-5 py-2.5 text-sm font-semibold text-white">
        Zur Slotbörse →
      </span>
    </Link>
  )
}

export default function AktionenClient({
  promotions,
  enabled,
  boerse,
}: {
  promotions: Promotion[]
  enabled: boolean
  boerse?: { enabled: boolean; maxDiscountPercent: number }
}) {
  const rules = promotions.filter((p) => p.kind === "RULE")
  const coupons = promotions.filter((p) => p.kind === "COUPON")
  const campaigns = promotions.filter((p) => p.kind === "CAMPAIGN")

  return (
    <div className="py-16">
      <div className="container-custom">
        {boerse?.enabled && <BoerseHint maxDiscountPercent={boerse.maxDiscountPercent} />}

        {promotions.length === 0 ? (
          <div className="mx-auto max-w-md rounded-2xl border border-gray-100 bg-white p-10 text-center shadow-sm">
            <Tag className="mx-auto mb-3 h-10 w-10 text-gray-300" />
            <p className="text-lg font-semibold text-gray-700">
              {enabled ? "Derzeit keine aktiven Aktionen" : "Derzeit keine öffentlichen Aktionen"}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {boerse?.enabled
                ? "Schau in der Slotbörse vorbei oder sichere dir jetzt deinen Termin."
                : "Schau bald wieder vorbei – oder sichere dir jetzt deinen Termin."}
            </p>
            <Link
              href="/buchen"
              className="mt-4 inline-block rounded-lg bg-[var(--main-color)] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              Jetzt buchen
            </Link>
          </div>
        ) : (
          <>
            {rules.length > 0 && (
              <section className="mb-12">
                <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-900">
                  <Tag className="h-5 w-5 text-amber-500" /> Preisaktionen
                </h2>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {rules.map((p, i) => (
                    <PromotionCard key={`rule-${i}`} promo={p} />
                  ))}
                </div>
              </section>
            )}

            {coupons.length > 0 && (
              <section className="mb-12">
                <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-900">
                  <Ticket className="h-5 w-5 text-violet-500" /> Gutscheincodes
                </h2>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {coupons.map((p, i) => (
                    <PromotionCard key={`coupon-${i}`} promo={p} />
                  ))}
                </div>
              </section>
            )}

            {campaigns.length > 0 && (
              <section>
                <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-900">
                  <Gift className="h-5 w-5 text-rose-500" /> Aktionen vor Ort
                </h2>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {campaigns.map((p, i) => (
                    <PromotionCard key={`campaign-${i}`} promo={p} />
                  ))}
                </div>
              </section>
            )}

            <div className="mt-12 text-center">
              <Link
                href="/buchen"
                className="inline-block rounded-lg bg-[var(--main-color)] px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90"
              >
                Jetzt Termin sichern
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
